import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectSettings } from "../settings/settingsSlice";
import { AICallMetrics, fetchAIRequestsForCompany } from "./aiRequestsApi";
import { showError } from "../notifications/notificationsSlice";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Alert, Box, IconButton, Paper, Stack, Typography } from "@mui/material";
import { LoadingContainer } from "../../app/LoadingContainer";
import { QuickSearchToolbar } from "../../app/QuickSearchToolbar";
import SyncIcon from '@mui/icons-material/Sync'
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { enUS, plPL } from '@mui/x-data-grid/locales'
import { Localized, useLocalization } from "@fluent/react";
import { DateFilterMenu } from "../documents/utils/DateFilterMenu";
import { BarChart } from "@mui/x-charts/BarChart";

const NoData = () => {
    return (
        <Box
            sx={{
                minHeight: 330,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                color: 'text.secondary',
                gap: 2,
            }}
        >
            <InsertChartOutlinedIcon sx={{ fontSize: 80, opacity: 0.6 }} />
            <Typography variant="h5" fontWeight={600}>
                <Localized id="np-data">_Brak danych</Localized>
            </Typography>
        </Box>
    );
}

const getCurrentMonthRange = () => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { from, to };
};

type UserUsageRow = {
    userId: string;
    requestsCount: number;
    totalTokens: number;
    sharePct: number;
};

type MonthRow = {
    key: string;
    date: Date;
    label: string;
    totalTokens: number;
    requestsCount: number;
    activeUsers: number;
};

export const ClientAIStatistics = () => {
    const { locale } = useAppSelector(selectSettings);
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch();

    const initialRange = useMemo(() => getCurrentMonthRange(), []);

    const [requests, setRequests] = useState<AICallMetrics[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [dateFrom, setDateFrom] = useState<Date | null>(initialRange.from);
    const [dateTo, setDateTo] = useState<Date | null>(initialRange.to);

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        }
        return defaultEnLocaleText;
    };

    const fetchStats = useCallback(async (dateFromParam?: Date | null, dateToParam?: Date | null) => {
        setIsLoading(true);
        try {
            const result = await dispatch(fetchAIRequestsForCompany({
                dateFrom: dateFromParam !== undefined ? dateFromParam || undefined : dateFrom || undefined,
                dateTo: dateToParam !== undefined ? dateToParam || undefined : dateTo || undefined,
            }));

            if (fetchAIRequestsForCompany.fulfilled.match(result)) {
                setRequests(result.payload as AICallMetrics[]);
            } else {
                dispatch(showError('error'));
            }
        } catch {
            dispatch(showError('error'));
        } finally {
            setIsLoading(false);
        }
    }, [dateFrom, dateTo, dispatch]);

    useEffect(() => {
        fetchStats(undefined, undefined);
    }, [fetchStats]);

    const handleMonthFilterSubmit = (dates) => {
        setDateFrom(dates.dateFrom);
        setDateTo(dates.dateTo);
    };

    const handleRefreshClick = () => {
        fetchStats(dateFrom, dateTo);
    };

    const selectedPeriodMessage = useMemo(() => {
        if (dateFrom && dateTo) {
            const from = dateFrom.toLocaleDateString(locale);
            const to = dateTo.toLocaleDateString(locale);
            if (locale === "pl") {
                return (
                    <>
                        Poniższe informacje dotyczą okresu od <strong>{from}</strong> do <strong>{to}</strong>.
                    </>
                );
            }
            if (locale === "de") {
                return (
                    <>
                        Die folgenden Informationen beziehen sich auf den Zeitraum vom <strong>{from}</strong> bis zum <strong>{to}</strong>.
                    </>
                );
            }

            return (
                <>
                    The following information covers the period from <strong>{from}</strong> to <strong>{to}</strong>.
                </>
            );
        }

        if(locale === "pl") return "Poniższe informacje obejmują cały dostępny okres.";
        if(locale === "de") return "Die folgenden Informationen beziehen sich auf den gesamten verfügbaren Zeitraum.";
        return "The information below covers the entire available period.";
    }, [dateFrom, dateTo, locale]);

    const summary = useMemo(() => {
        const totals = requests.reduce((acc, stat) => {
            acc.inputTokens += stat.inputTokens;
            acc.outputTokens += stat.outputTokens;
            acc.totalTokens += stat.totalTokens;
            return acc;
        }, { inputTokens: 0, outputTokens: 0, totalTokens: 0 });

        return {
            ...totals,
            requestsCount: requests.length
        };
    }, [requests]);

    const activeUsersCount = useMemo(() => {
        return new Set(requests.map((request) => request.userId)).size;
    }, [requests]);

    const promptUsage = useMemo(() => {
        const byPrompt = requests.reduce((acc, stat) => {
            const promptKey = stat.promptKey.trim();
            const existing = acc.get(promptKey);
            if (existing) {
                existing.requestsCount += 1;
                existing.totalTokens += stat.totalTokens;
                existing.users.add(stat.userId);
            } else {
                acc.set(promptKey, {
                    promptKey,
                    requestsCount: 1,
                    totalTokens: stat.totalTokens,
                    users: new Set([stat.userId]),
                });
            }
            return acc;
        }, new Map<string, { promptKey: string; requestsCount: number; totalTokens: number; users: Set<string> }>());

        return Array.from(byPrompt.values())
            .map((item) => ({
                promptKey: item.promptKey,
                requestsCount: item.requestsCount,
                totalTokens: item.totalTokens,
                activeUsers: item.users.size,
            }))
            .sort((a, b) => b.requestsCount - a.requestsCount);
    }, [requests]);

    const topPromptByRequests = useMemo(() => {
        return promptUsage[0] ?? null;
    }, [promptUsage]);

    const topPromptByTokens = useMemo(() => {
        return [...promptUsage].sort((a, b) => b.totalTokens - a.totalTokens)[0] ?? null;
    }, [promptUsage]);

    const usageByUser = useMemo(() => {
        return requests.reduce((acc, stat) => {
            const existing = acc.find((row) => row.userId === stat.userId);
            if (existing) {
                existing.requestsCount += 1;
                existing.totalTokens += stat.totalTokens;
            } else {
                acc.push({
                    userId: stat.userId,
                    requestsCount: 1,
                    totalTokens: stat.totalTokens,
                    sharePct: 0,
                });
            }
            return acc;
        }, [] as UserUsageRow[])
            .map((row) => ({
                ...row,
                sharePct: summary.requestsCount > 0 ? (row.requestsCount / summary.requestsCount) * 100 : 0,
            }))
            .sort((a, b) => b.sharePct - a.sharePct);
    }, [requests, summary.requestsCount]);

    const usageColumns: GridColDef<UserUsageRow>[] = [
        {
            field: 'userId',
            headerName: l10n.getString("user", null, "_Użytkownik"),
            flex: 1
        },
        {
            field: 'requestsCount',
            headerName: l10n.getString("ai-settings-requests", null, "_Zapytania do AI"),
            width: 150
        },
        {
            field: 'sharePct',
            headerName: l10n.getString("ai-settings-percent-of-all-requests", null, "_% wszystkich zapytań"),
            width: 170,
            valueFormatter: (value) => `${Number(value ?? 0).toFixed(1)}%`
        },
        {
            field: 'impact',
            headerName: l10n.getString("ai-settings-impact", null, "_Wpływ"),
            width: 100,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            renderCell: (params) => {
                const sharePct = Number(params.row.sharePct ?? 0);
                if (sharePct >= 40) {
                    return (
                        <Box sx={{ color: 'error.main', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ErrorOutlineIcon fontSize="small" />
                        </Box>
                    );
                }

                if (sharePct >= 25) {
                    return (
                        <Box sx={{ color: 'warning.main', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ErrorOutlineIcon fontSize="small" />
                        </Box>
                    );
                }

                return <></>;
            }
        },
        {
            field: 'totalTokens',
            headerName: l10n.getString("ai-settings-total-tokens", null, "_Liczba tokenów"),
            width: 120
        },
    ];

    const monthlyUsage = useMemo(() => {
        const byMonth = requests.reduce((acc, stat) => {
            const date = new Date(stat.occurredAt);
            const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

            const existing = acc.get(key);
            if (existing) {
                existing.totalTokens += stat.totalTokens;
                existing.requestsCount += 1;
                existing.users.add(stat.userId);
            } else {
                acc.set(key, {
                    key,
                    date,
                    label: date.toLocaleDateString(locale, { month: 'short', year: 'numeric' }),
                    totalTokens: stat.totalTokens,
                    requestsCount: 1,
                    users: new Set([stat.userId]),
                });
            }
            return acc;
        }, new Map<string, { key: string; date: Date; label: string; totalTokens: number; requestsCount: number; users: Set<string> }>());

        return Array.from(byMonth.values())
            .sort((a, b) => a.key.localeCompare(b.key))
            .map((item) => ({
                key: item.key,
                date: item.date,
                label: item.label,
                totalTokens: item.totalTokens,
                requestsCount: item.requestsCount,
                activeUsers: item.users.size,
            }));
    }, [locale, requests]) as MonthRow[];

    const avgOperationsPerMonth = useMemo(() => {
        if (monthlyUsage.length === 0) {
            return 0;
        }
        const totalMonthlyOps = monthlyUsage.reduce((acc, month) => acc + month.requestsCount, 0);
        return totalMonthlyOps / monthlyUsage.length;
    }, [monthlyUsage]);

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
                <DateFilterMenu
                    currentOption={"currentMonth"}
                    onSubmit={handleMonthFilterSubmit}
                />
                <IconButton onClick={handleRefreshClick}>
                    <SyncIcon />
                </IconButton>
            </Stack>

            <Alert severity="info" variant="outlined">
                {selectedPeriodMessage}
            </Alert>

            {isLoading ? (
                <LoadingContainer />
            ) : (
                <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(5, 1fr)' }, gap: 2 }}>
                        <Paper sx={{ padding: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                <Localized id="ai-settings-requests">_Zapytania do AI</Localized>
                            </Typography>
                            <Typography variant="h5">{summary.requestsCount}</Typography>
                        </Paper>

                        <Paper sx={{ padding: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                <Localized id="ai-settings-active-users">_Aktywni użytkownicy</Localized>
                            </Typography>
                            <Typography variant="h5">{activeUsersCount}</Typography>
                        </Paper>

                        <Paper sx={{ padding: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                <Localized id="ai-settings-most-used-prompt">_Najpopularniejsze zapytanie</Localized>
                            </Typography>
                            <Typography variant="h6" noWrap>{topPromptByRequests?.promptKey ?? '-'}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                <Localized id="requests">_zapytań</Localized>: {topPromptByRequests?.requestsCount ?? 0}
                            </Typography>
                        </Paper>

                        <Paper sx={{ padding: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                <Localized id="ai-settings-highest-token-consumption">_Największe zużycie tokenów</Localized>
                            </Typography>
                            <Typography variant="h6" noWrap>{topPromptByTokens?.promptKey ?? '-'}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                <Localized id="ai-settings-total-tokens">_tokenów</Localized>: {topPromptByTokens?.totalTokens ?? 0}
                            </Typography>
                        </Paper>

                        <Paper sx={{ padding: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                <Localized id="ai-settings-total-tokens">_Liczba tokenów</Localized>
                            </Typography>
                            <Typography variant="h5">{summary.totalTokens}</Typography>
                        </Paper>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' },
                            gap: 2,
                            alignItems: 'stretch',
                        }}
                    >
                        <Paper sx={{ padding: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ marginBottom: 2 }}>
                                <Localized id="ai-settings-use-by-users">_Użycie przez użytkowników</Localized>
                            </Typography>
                            <DataGrid
                                style={{ minHeight: 100, height: 420 }}
                                localeText={getCustomLocaleText()}
                                rows={usageByUser}
                                columns={usageColumns}
                                getRowId={(row) => row.userId}
                                slots={{ toolbar: QuickSearchToolbar }}
                            />
                        </Paper>

                        <Paper sx={{ padding: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ marginBottom: 2 }}>
                                <Localized id="ai-settings-monthly-usage">_Miesięczne użycie</Localized>
                            </Typography>
                            {monthlyUsage.length > 0 ? (
                                <BarChart
                                    xAxis={[{ scaleType: 'band', data: monthlyUsage.map((m) => m.label) }]}
                                    series={[{ data: monthlyUsage.map((m) => m.totalTokens), label: l10n.getString("ai-settings-total-tokens", null, "_Liczba tokenów") }]}
                                    height={360}
                                    margin={{ top: 50, right: 16, bottom: 40, left: 48 }}
                                />
                            ) : (
                                <NoData/>
                            )}
                        </Paper>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' },
                            gap: 2,
                            alignItems: 'stretch',
                        }}
                    >
                        <Paper sx={{ padding: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ marginBottom: 1 }}>
                                <Localized id="ai-settings-average-operations">_Średnia liczba operacji</Localized>
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
                                <Localized id="average">_Średnia</Localized>: <strong>{avgOperationsPerMonth.toFixed(1)}</strong>
                            </Typography>
                            {monthlyUsage.length > 0 ? (
                                <BarChart
                                    xAxis={[{ scaleType: 'band', data: monthlyUsage.map((m) => m.label) }]}
                                    yAxis={[{ id: 'left-axis', scaleType: 'linear', valueFormatter: (value) => Number.isInteger(value) ? `${value}` : '' }]}
                                    series={[{ yAxisKey: 'left-axis', data: monthlyUsage.map((m) => m.requestsCount), label: l10n.getString("ai-settings-operations", null, "_Operacje AI") }]}
                                    height={330}
                                    margin={{ top: 50, right: 16, bottom: 40, left: 48 }}
                                />
                            ) : (
                                <NoData/>
                            )}
                        </Paper>

                        <Paper sx={{ padding: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ marginBottom: 2 }}>
                                <Localized id="ai-settings-active-users">_Aktywni użytkownicy</Localized>
                            </Typography>
                            {monthlyUsage.length > 0 ? (
                                <BarChart
                                    xAxis={[{ scaleType: 'band', data: monthlyUsage.map((m) => m.label) }]}
                                    yAxis={[{ id: 'left-axis', scaleType: 'linear', valueFormatter: (value) => Number.isInteger(value) ? `${value}` : '' }]}
                                    series={[{ yAxisKey: 'left-axis', data: monthlyUsage.map((m) => m.activeUsers), label: l10n.getString("ai-settings-active-users", null, "_Aktywni użytkownicy") }]}
                                    height={330}
                                    margin={{ top: 50, right: 16, bottom: 40, left: 48 }}
                                />
                            ) : (
                                <NoData/>
                            )}
                        </Paper>
                    </Box>

                    <Paper sx={{ padding: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ marginBottom: 2 }}>
                            <Localized id="ai-settings-popularity-feature">_Popularność funkcjonalności</Localized>
                        </Typography>
                        {promptUsage.length > 0 ? (
                            <BarChart
                                xAxis={[{ scaleType: 'band', data: promptUsage.slice(0, 10).map((m) => m.promptKey) }]}
                                yAxis={[
                                    { id: 'left-axis', scaleType: 'linear', valueFormatter: (value) => Number.isInteger(value) ? `${value}` : '' },
                                    { id: 'right-axis', scaleType: 'linear' }
                                ]}
                                series={[
                                    {
                                        yAxisKey: 'left-axis',
                                        data: promptUsage.slice(0, 10).map((m) => m.activeUsers),
                                        label: l10n.getString("ai-settings-users-count", null, "_Liczba użytkowników")
                                    },
                                    {
                                        yAxisKey: 'left-axis',
                                        data: promptUsage.slice(0, 10).map((m) => m.requestsCount),
                                        label: l10n.getString("ai-settings-requests", null, "_Liczba zapytań"),
                                        color: '#1565c0',
                                    },
                                    {
                                        yAxisKey: 'right-axis',
                                        data: promptUsage.slice(0, 10).map((m) => m.totalTokens),
                                        label: l10n.getString("ai-settings-total-tokens", null, "_Liczba tokenów"),
                                        color: '#ef6c00',
                                    },
                                ]}
                                rightAxis="right-axis"
                                height={420}
                                margin={{ top: 50, right: 56, bottom: 40, left: 56 }}
                            />
                        ) : (
                            <NoData/>
                        )}
                    </Paper>
                </>
            )}
        </Stack>
    );
};
